import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const TeacherGrades = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Mode : 'list' (choix) ou 'grade' (saisie)
  const [mode, setMode] = useState('list');
  const [selectedEval, setSelectedEval] = useState(null);

  // Formulaire nouvelle évaluation
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('devoir');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [coefficient, setCoefficient] = useState(2);
  const [maxScore, setMaxScore] = useState(20);
  const [description, setDescription] = useState('');

  const [grades, setGrades] = useState({});

  // Filtres liste
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classesRes, subjectsRes, evalsRes] = await Promise.all([
        api.get('/teacher/classes', { headers }),
        api.get('/teacher/subjects', { headers }),
        api.get('/teacher/evaluations', { headers }),
      ]);

      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
      setEvaluations(Array.isArray(evalsRes.data) ? evalsRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/teacher/classes/${classId}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studentsList = Array.isArray(res.data) ? res.data : [];
      setStudents(studentsList);

      const initialGrades = {};
      studentsList.forEach((s) => {
        initialGrades[s.id] = { score: '', appreciation: '', is_published: false };
      });
      setGrades(initialGrades);
    } catch (err) {
      console.error('Erreur:', err);
      setStudents([]);
    }
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    if (classId) loadStudents(classId);
    else setStudents([]);
  };

  const handleGradeChange = (studentId, field, value) => {
    if (field === 'score') {
      const num = parseFloat(value);
      if (value !== '' && (isNaN(num) || num < 0 || num > parseFloat(maxScore))) {
        return;
      }
    }
    setGrades((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  // ✅ Ouvrir une évaluation existante pour la modifier
  const openEvaluation = async (ev) => {
    try {
      setMode('grade');
      setSelectedEval(ev);
      setSelectedClass(ev.class_id);
      setSelectedSubject(ev.subject_id);
      setTitle(ev.title);
      setType(ev.type);
      setDate(ev.date);
      setCoefficient(ev.coefficient);
      setMaxScore(ev.max_score);

      const token = localStorage.getItem('token');
      const res = await api.get(`/teacher/evaluations/${ev.id}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const list = res.data.students || [];
      setStudents(list);

      const initial = {};
      list.forEach((s) => {
        initial[s.id] = {
          score: s.score ?? '',
          appreciation: s.appreciation ?? '',
          is_published: s.is_published ?? false,
        };
      });
      setGrades(initial);
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du chargement');
    }
  };

  // ✅ Réinitialiser pour créer une nouvelle évaluation
  const resetToNew = () => {
    setMode('list');
    setSelectedEval(null);
    setSelectedClass('');
    setSelectedSubject('');
    setTitle('');
    setType('devoir');
    setDate(new Date().toISOString().split('T')[0]);
    setCoefficient(2);
    setMaxScore(20);
    setDescription('');
    setStudents([]);
    setGrades({});
  };

  // ✅ Enregistrer une NOUVELLE évaluation + notes
  const handleSaveNew = async (status) => {
    if (!selectedClass || !selectedSubject || !title) {
      alert('Veuillez remplir toutes les informations');
      return;
    }

    const missingScores = students.filter((s) => grades[s.id]?.score === '' || grades[s.id]?.score === undefined);
    if (missingScores.length > 0) {
      if (!window.confirm(`${missingScores.length} élève(s) sans note. Continuer ?`)) return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const evalRes = await api.post('/teacher/evaluations', {
        class_id: selectedClass,
        subject_id: selectedSubject,
        title,
        type,
        date,
        coefficient: parseFloat(coefficient),
        max_score: parseFloat(maxScore),
        description,
      }, { headers });

      const evaluationId = evalRes.data.data.id;

      const gradesPayload = students
        .filter((s) => grades[s.id]?.score !== '' && grades[s.id]?.score !== undefined)
        .map((s) => ({
          student_id: s.id,
          score: parseFloat(grades[s.id].score),
          appreciation: grades[s.id].appreciation || null,
        }));

      await api.post('/teacher/grades', {
        evaluation_id: evaluationId,
        grades: gradesPayload,
        status,
      }, { headers });

      alert(status === 'submitted' ? '✅ Notes soumises !' : '💾 Brouillon enregistré !');
      resetToNew();
      fetchInitialData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ✅ Enregistrer les notes d'une évaluation EXISTANTE
  const handleSaveExisting = async (status) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const gradesPayload = students
        .filter((s) => grades[s.id]?.score !== '' && grades[s.id]?.score !== undefined)
        .map((s) => ({
          student_id: s.id,
          score: parseFloat(grades[s.id].score),
          appreciation: grades[s.id].appreciation || null,
        }));

      await api.post('/teacher/grades', {
        evaluation_id: selectedEval.id,
        grades: gradesPayload,
        status,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(status === 'submitted' ? '✅ Notes soumises !' : '💾 Brouillon enregistré !');
      resetToNew();
      fetchInitialData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ✅ Stats temps réel
  const stats = useMemo(() => {
    const scores = Object.values(grades)
      .map((g) => parseFloat(g.score))
      .filter((s) => !isNaN(s));
    if (scores.length === 0) return { count: 0, average: 0, best: 0, worst: 0, missing: students.length };
    return {
      count: scores.length,
      average: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2),
      best: Math.max(...scores).toFixed(2),
      worst: Math.min(...scores).toFixed(2),
      missing: students.length - scores.length,
    };
  }, [grades, students]);

  // ✅ Filtre
  const filteredEvaluations = useMemo(() => {
    return evaluations.filter((ev) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        if (
          !(ev.title || '').toLowerCase().includes(t) &&
          !(ev.class_name || '').toLowerCase().includes(t) &&
          !(ev.subject_name || '').toLowerCase().includes(t)
        ) return false;
      }
      if (filterType !== 'all' && ev.type !== filterType) return false;
      return true;
    });
  }, [evaluations, searchTerm, filterType]);

  const getTypeBadge = (type) => {
    switch (type) {
      case 'devoir': return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">📝 Devoir</span>;
      case 'interrogation': return <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">❓ Interro</span>;
      case 'composition': return <span className="text-xs bg-orange-100 text-[#E67E22] px-2 py-0.5 rounded-full">🧪 Composition</span>;
      default: return null;
    }
  };

  const getStatusBadge = (ev) => {
    if (ev.status === 'published') {
      return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">🟢 Publiée</span>;
    }
    return <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">🟡 Brouillon</span>;
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">✏️ Saisir les notes</h1>
        <p className="text-gray-500">Créez une évaluation ou ouvrez-en une existante</p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Les notes sont envoyées à l'administration pour validation.
          Les élèves et parents les voient après validation.
        </p>
      </div>

      {/* ═══════════ ÉTAPE 1 : LISTE ═══════════ */}
      {mode === 'list' && (
        <>
          {/* Mes évaluations existantes */}
          {evaluations.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                <h3 className="font-bold text-[#2C3E50]">📋 Mes évaluations existantes ({evaluations.length})</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  >
                    <option value="all">Tous</option>
                    <option value="devoir">📝 Devoirs</option>
                    <option value="interrogation">❓ Interros</option>
                    <option value="composition">🧪 Compos</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredEvaluations.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{ev.title}</span>
                        {getTypeBadge(ev.type)}
                        {getStatusBadge(ev)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        🏫 {ev.class_name} • 📚 {ev.subject_name} • 📅 {ev.date_display} • ⚖️ Coef {ev.coefficient}
                      </p>
                    </div>
                    <button
                      onClick={() => openEvaluation(ev)}
                      className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition font-medium whitespace-nowrap"
                    >
                      ✏️ Ouvrir
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulaire nouvelle évaluation */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-bold text-[#2C3E50] mb-4">➕ Créer une nouvelle évaluation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Classe *</label>
                <select
                  value={selectedClass}
                  onChange={handleClassChange}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">-- Choisir --</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Matière *</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">-- Choisir --</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="devoir">📝 Devoir</option>
                  <option value="interrogation">❓ Interrogation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Devoir 1"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Barème *</label>
                <select
                  value={maxScore}
                  onChange={(e) => setMaxScore(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="10">/10</option>
                  <option value="20">/20</option>
                  <option value="100">/100</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Coefficient *</label>
                <input
                  type="number"
                  value={coefficient}
                  onChange={(e) => setCoefficient(e.target.value)}
                  min="0.5"
                  step="0.5"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            {students.length > 0 && (
              <div className="mt-6">
                <h4 className="font-bold text-[#2C3E50] mb-3">👨‍🎓 Notes des élèves ({students.length})</h4>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Saisies</p>
                    <p className="text-lg font-bold text-green-600">{stats.count}/{students.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Restantes</p>
                    <p className="text-lg font-bold text-orange-600">{stats.missing}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Moyenne</p>
                    <p className="text-lg font-bold text-[#2C3E50]">{stats.average}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Meilleure</p>
                    <p className="text-lg font-bold text-blue-600">{stats.best}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Plus faible</p>
                    <p className="text-lg font-bold text-red-600">{stats.worst}</p>
                  </div>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Élève</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Note /{maxScore}</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Appréciation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id} className="border-t">
                          <td className="py-2 px-3 font-medium">{student.name}</td>
                          <td className="py-2 px-3">
                            <input
                              type="number"
                              value={grades[student.id]?.score ?? ''}
                              onChange={(e) => handleGradeChange(student.id, 'score', e.target.value)}
                              min="0"
                              max={maxScore}
                              step="0.25"
                              placeholder="--"
                              className="w-24 px-2 py-1 border rounded-lg text-center"
                            />
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="text"
                              value={grades[student.id]?.appreciation ?? ''}
                              onChange={(e) => handleGradeChange(student.id, 'appreciation', e.target.value)}
                              placeholder="Ex: Bien"
                              className="w-full px-2 py-1 border rounded-lg"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={resetToNew}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleSaveNew('draft')}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                  >
                    💾 Brouillon
                  </button>
                  <button
                    onClick={() => handleSaveNew('submitted')}
                    disabled={saving}
                    className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : '📤 Soumettre à l\'admin'}
                  </button>
                </div>
              </div>
            )}

            {students.length === 0 && selectedClass && (
              <p className="mt-4 text-sm text-gray-500 text-center">Aucun élève dans cette classe</p>
            )}
          </div>
        </>
      )}

      {/* ═══════════ ÉTAPE 2 : SAISIE ═══════════ */}
      {mode === 'grade' && selectedEval && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <button
            onClick={resetToNew}
            className="text-sm text-gray-500 hover:text-[#E67E22] mb-3 flex items-center space-x-1"
          >
            <span>←</span>
            <span>Retour à la liste</span>
          </button>

          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h2 className="text-lg font-bold text-[#2C3E50]">{selectedEval.title}</h2>
            {getTypeBadge(selectedEval.type)}
            {getStatusBadge(selectedEval)}
          </div>
          <p className="text-sm text-gray-500 mb-4">
            🏫 {selectedEval.class_name} • 📚 {selectedEval.subject_name} • 📅 {selectedEval.date_display} • ⚖️ Coef {selectedEval.coefficient}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-gray-500">Saisies</p>
              <p className="text-lg font-bold text-green-600">{stats.count}/{students.length}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Restantes</p>
              <p className="text-lg font-bold text-orange-600">{stats.missing}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Moyenne</p>
              <p className="text-lg font-bold text-[#2C3E50]">{stats.average}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Meilleure</p>
              <p className="text-lg font-bold text-blue-600">{stats.best}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Plus faible</p>
              <p className="text-lg font-bold text-red-600">{stats.worst}</p>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Note /{selectedEval.max_score}</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Appréciation</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const g = grades[s.id] || {};
                  const locked = g.is_published;
                  return (
                    <tr key={s.id} className={`border-t ${locked ? 'bg-green-50' : ''}`}>
                      <td className="py-2 px-3 font-medium">{s.name}</td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          value={g.score ?? ''}
                          onChange={(e) => handleGradeChange(s.id, 'score', e.target.value)}
                          min="0"
                          max={selectedEval.max_score}
                          step="0.25"
                          disabled={locked}
                          placeholder="--"
                          className={`w-24 px-2 py-1 border rounded-lg text-center ${locked ? 'bg-gray-100' : ''}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          value={g.appreciation ?? ''}
                          onChange={(e) => handleGradeChange(s.id, 'appreciation', e.target.value)}
                          disabled={locked}
                          placeholder="Ex: Bien"
                          className={`w-full px-2 py-1 border rounded-lg ${locked ? 'bg-gray-100' : ''}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        {locked ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🔒 Validée</span>
                        ) : g.score !== '' && g.score !== undefined ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">📝 Saisie</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={resetToNew}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => handleSaveExisting('draft')}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              💾 Brouillon
            </button>
            <button
              onClick={() => handleSaveExisting('submitted')}
              disabled={saving}
              className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : '📤 Soumettre à l\'admin'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherGrades;